import { ChevronDown, ChevronRight } from "lucide-react";
import React, { useState } from "react";
import { graphql, useLazyLoadQuery, useMutation } from "react-relay";
import { Button } from "src/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "src/components/ui/dialog";
import IntegrationIcon from "src/components/ui/IntegrationIcon";
import { ErrorAlert } from "src/components/ui/status-alert";
import { usePermissions } from "src/context/MeContext";
import { getProductName } from "src/utils/brandTheme";

import type { PGAnalyzeIntegrationDeleteMutation } from "./__generated__/PGAnalyzeIntegrationDeleteMutation.graphql";
import type { PGAnalyzeIntegrationQuery } from "./__generated__/PGAnalyzeIntegrationQuery.graphql";
import PGAnalyzeIntegrationForm from "./PGAnalyzeIntegrationForm";

const pgAnalyzeIntegrationQuery = graphql`
  query PGAnalyzeIntegrationQuery {
    me {
      ...appMe
      organization {
        integrations(integrationType: PGANALYZE) {
          edges {
            node {
              id
              credentials
            }
          }
        }
      }
    }
  }
`;

const deleteIntegrationMutation = graphql`
  mutation PGAnalyzeIntegrationDeleteMutation($input: DeleteIntegrationInput!) {
    deleteIntegration(input: $input) {
      success
      error
    }
  }
`;

interface PGAnalyzeIntegrationProps {
  isOpen: boolean;
  onClose: () => void;
  onRefetch?: () => void;
  brandTheme?: string | null;
}

const PGAnalyzeInstructions: React.FC<{ productName: string }> = ({ productName }) => {
  return (
    <div className="text-muted-foreground space-y-3">
      <div className="mb-4">
        <p className="mb-2 font-bold">API Key Setup:</p>
        <ol className="text-sm space-y-2 list-decimal list-inside">
          <li>
            Log into your{" "}
            <a
              href="https://app.pganalyze.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-amber-600 hover:underline"
            >
              PGAnalyze account
            </a>
          </li>
          <li>
            Navigate to your organization&apos;s <span className="text-amber-600">Settings</span>
            {" > "}
            <span className="text-amber-600">API Keys</span>
          </li>
          <li>
            Click <span className="text-amber-600">Create new API Key</span>
          </li>
          <li>
            Select <span className="text-amber-600">Read-Only API Key</span> type
          </li>
          <li>
            Copy the generated key and paste it into the API Key field below
          </li>
          <li>
            Enter your organization slug (visible in your PGAnalyze URL: <span className="text-amber-600">app.pganalyze.com/organizations/your-slug</span>)
          </li>
          <li>
            For Enterprise Server deployments, set the Base URL to your internal hostname
          </li>
        </ol>
        <p className="text-sm mt-3">
          {productName} uses the PGAnalyze API to retrieve server information, query performance statistics, and check-up issues.
        </p>
      </div>
    </div>
  );
};

interface PGAnalyzeIntegrationContentProps {
  onClose: () => void;
  onRefetch?: () => void;
  productName: string;
}

function PGAnalyzeIntegrationContent({ onClose, onRefetch, productName }: PGAnalyzeIntegrationContentProps) {
  const [showInstructions, setShowInstructions] = useState(false);
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);
  const [disconnectError, setDisconnectError] = useState<string | null>(null);
  const { canWrite } = usePermissions();
  const canModify = canWrite("integrations");

  const data = useLazyLoadQuery<PGAnalyzeIntegrationQuery>(
    pgAnalyzeIntegrationQuery,
    {},
    { fetchPolicy: "network-only" },
  );

  const existingIntegration = data.me.organization?.integrations?.edges?.[0]?.node;
  const existingCredentials = existingIntegration?.credentials as {
    api_key?: string;
    organization_slug?: string;
    base_url?: string;
  } | null;
  const isConnected = !!existingIntegration;

  const [commitDelete, isDeleting] =
    useMutation<PGAnalyzeIntegrationDeleteMutation>(deleteIntegrationMutation);

  const handleDisconnectConfirm = () => {
    if (!existingIntegration?.id) return;
    setDisconnectError(null);
    commitDelete({
      variables: { input: { id: existingIntegration.id } },
      onCompleted: (response) => {
        if (response.deleteIntegration.error) {
          setDisconnectError(response.deleteIntegration.error);
          return;
        }
        setShowDisconnectConfirm(false);
        onClose();
        onRefetch?.();
      },
      onError: (error) => {
        setDisconnectError(error.message);
      },
    });
  };

  const handleFormComplete = (success: boolean) => {
    if (success) {
      onClose();
      onRefetch?.();
    }
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-3">
          <IntegrationIcon type="PGANALYZE" className="h-6 w-6" />
          {isConnected ? "Manage PGAnalyze" : "Connect PGAnalyze"}
        </DialogTitle>
        <DialogDescription>
          {isConnected
            ? `Update your PGAnalyze integration settings for ${productName}.`
            : `Connect your PGAnalyze account to give ${productName} access to your PostgreSQL performance insights.`}
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-6 overflow-y-auto max-h-[80vh] p-1">
        <div className="space-y-3">
          <button
            onClick={() => setShowInstructions(!showInstructions)}
            className="flex items-center gap-2 text-primary hover:text-primary/80 transition-colors"
          >
            {showInstructions ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
            {showInstructions ? "Hide instructions" : "Show instructions"}
          </button>

          <div className={`overflow-hidden transition-all duration-300 ease-in-out ${
            showInstructions
              ? "max-h-[2000px] opacity-100"
              : "max-h-0 opacity-0"
          }`}>
            <PGAnalyzeInstructions productName={productName} />
          </div>
        </div>

        <PGAnalyzeIntegrationForm
          onComplete={handleFormComplete}
          onCancel={onClose}
          onDisconnect={isConnected && canModify ? () => setShowDisconnectConfirm(true) : undefined}
          integrationId={existingIntegration?.id}
          initialApiKey={existingCredentials?.api_key}
          initialOrganizationSlug={existingCredentials?.organization_slug}
          initialBaseUrl={existingCredentials?.base_url}
        />
      </div>

      <Dialog open={showDisconnectConfirm} onOpenChange={() => setShowDisconnectConfirm(false)}>
        <DialogContent className="sm:max-w-md bg-popover text-foreground border-border">
          <DialogHeader>
            <DialogTitle>Disconnect PGAnalyze</DialogTitle>
            <DialogDescription>
              This will remove the PGAnalyze integration and all associated credentials. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          {disconnectError && <ErrorAlert icon="none">{disconnectError}</ErrorAlert>}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDisconnectConfirm(false)} disabled={isDeleting}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDisconnectConfirm} disabled={isDeleting}>
              {isDeleting ? "Disconnecting..." : "Disconnect"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function PGAnalyzeIntegration({ isOpen, onClose, onRefetch, brandTheme }: PGAnalyzeIntegrationProps) {
  const productName = getProductName(brandTheme);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-4xl bg-popover text-foreground border-border">
        {isOpen && (
          <PGAnalyzeIntegrationContent
            onClose={onClose}
            onRefetch={onRefetch}
            productName={productName}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
